import React, { useState } from 'react';
import useFieldApi, { UseFieldApiConfig } from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import { FieldWrapper } from './FieldWrapper';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Tooltip } from './Tooltip';
import { isRequiredValidation } from '../../utils';

interface Props {
  name: string;
  wrapperClasses?: string;
  label?: string;
  placeholder?: string;
  toolTip?: string;
  fieldClasses?: string;
  isError?: boolean;
  error?: string;
  Right?: React.ReactNode;
  showAsPlainText?: boolean;
  errorClassName?: string;
  [key: string]: any;
}

export const TextInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      label,
      placeholder,
      toolTip,
      fieldClasses,
      isError,
      Right,
      wrapperClassName,
      showAsPlainText = false,
      dropdown,
      ...input
    }: Props,
    ref: React.Ref<HTMLInputElement>
  ) => {
    // Overflow text state
    const [hasOverflowingChildren, setHasOverflowingChildren] = useState<boolean>(false);

    // On Mouse Over, set whether to show tooltip
    const handleMouseOverOverflow = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const el = e.target as HTMLInputElement;
      setHasOverflowingChildren(el?.scrollWidth > el?.clientWidth);
    };

    return (
      <div
        className={clsx(
          'bg-white',
          !dropdown && 'relative',
          !showAsPlainText && 'field-with-icons',
          isError && 'shadow-field-error',
          wrapperClassName
        )}
        onMouseOver={handleMouseOverOverflow}
        data-testid='text-input-wrapper'
      >
        <input
          id={name}
          name={name}
          type='text'
          placeholder={placeholder}
          className={clsx(
            'truncate',
            showAsPlainText
              ? 'bg-white appearance-none font-effra font-normal text-black-default'
              : 'form-field placeholder-gray-default mb-0 border-0 focus:border-0',
            fieldClasses,
            Right && 'rounded-r-none',
            input.disabled && 'text-gray-30'
          )}
          style={
            Right
              ? // stretch the input to its full length, taking into account the button on the right
                { width: 'calc(100% - 40px)' }
              : undefined
          }
          autoComplete='off'
          {...input}
          ref={ref}
          aria-describedby={name && `error-for-${name}`}
          aria-invalid={isError}
          aria-required={input.required}
          data-tip={input.value}
          data-for={`toolTip-${name}`}
          required={input.required}
          disabled={input.disabled || showAsPlainText}
        />
        {Right && (
          <div
            className={clsx(
              'absolute right-0 flex items-center h-full space-x-4 bg-transparent',
              input.disabled && 'text-gray-30'
            )}
          >
            {Right}
          </div>
        )}
        <Tooltip id={`toolTip-${name}`} place='right' visible={hasOverflowingChildren} />
      </div>
    );
  }
);

export const TextField = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      wrapperClasses,
      label,
      toolTip,
      isError,
      error,
      validate,
      component,
      additionalLabelClasses,
      errorClassName,
      ...props
    }: Props,
    ref
  ) => {
    const { t } = useTranslation('form');
    const isRequired = isRequiredValidation(validate);

    return (
      <FieldWrapper
        className={wrapperClasses}
        errorClassName={errorClassName}
        additionalLabelClasses={additionalLabelClasses}
        toolTip={toolTip}
        name={name}
        label={label ? t(label) : label}
        isError={isError}
        error={error}
      >
        <TextInput {...props} name={name} ref={ref} isError={isError} required={isRequired} />
      </FieldWrapper>
    );
  }
);

/* istanbul ignore next */ /* this code is not used or will be deleted in other branches */
export function TextFieldDdf({ colSpan, colWidth, colStart, ...props }: UseFieldApiConfig) {
  const { input, meta } = useFieldApi(props);
  const { modified, error } = meta;
  const isError = modified && error && typeof error === 'string';

  return <TextField {...props} {...input} isError={isError} error={error} />;
}
